import React, { useState } from "react";
import { Input, Form, Col, Row, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";

import ProductRecomHighlightTable from "./ProductRecomHighlightTable";
import ProductRecomNomaroiTable from "./ProductRecomNomaroiTable";
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from "@headlessui/react";

// Custom Hooks
import useTableController from "hooks/useTableController";

// Validation
import { CategoryBrandFilterSchema } from "model/_base/filters/filter-options.schema";

// API
import { useGetProdRecomHighlightQuery, useGetProdRecomNomaroiQuery } from "services/contents/api";
import { useGetFilterOptionsQuery } from "services/filters/api";

const ProductRecomTab = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  // HOOK CHANGE
  const { formik, searchInput, setSearchInput, handleTableChange, handleFieldChange } = useTableController({
    initialValues: {
      filterType: "all",
      categoryId: "",
      brandId: "",
      searchTerm: "",
    },
    validationSchema: CategoryBrandFilterSchema,
  });

  // State for filter options
  const [options, setOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  // API
  const { data: filterOptionsData } = useGetFilterOptionsQuery();
  const { data: dataHighlight, isLoading: isLoadingHighlight, error: errorHighlight } = useGetProdRecomHighlightQuery(formik.values);
  const { data: dataNomaroi, isLoading: isLoadingNomaroi, error: errorNomaroi } = useGetProdRecomNomaroiQuery(formik.values);

  // Update state when filterOptionsData changes
  React.useEffect(() => {
    if (filterOptionsData) {
      setOptions(filterOptionsData.options || []);
      setCategories(filterOptionsData.categories || []);
      setBrands(filterOptionsData.brands || []);
    }
  }, [filterOptionsData]);

  // ACTIONS
  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);
  const toggleCategoryDropdown = () => setCategoryDropdownOpen((prevState) => !prevState);
  const toggleBrandDropdown = () => setBrandDropdownOpen((prevState) => !prevState);

  const handleOptionSelect = (option) => {
    handleFieldChange("filterType", option.id);
    if (option.id === "category") {
      handleFieldChange("categoryId", categories[0].id);
    } else {
      handleFieldChange("brandId", brands[0].id);
    }
  };

  const handleCategorySelect = (category) => {
    handleFieldChange("categoryId", category.id);
  };

  const handleBrandSelect = (brand) => {
    handleFieldChange("brandId", brand.id);
  };

  // Get selected option, category and brand based on formik values
  const selectedOption = options.find((o) => o.id === formik.values.filterType) || options[0];
  const selectedCategory = categories.find((c) => c.id === formik.values.categoryId) || categories[0];
  const selectedBrand = brands.find((b) => b.id === formik.values.brandId) || brands[0];

  // RENDER
  return (
    <Form
      className="form-horizontal w-100 d-flex flex-column gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        formik.handleSubmit();
      }}
      data-testid="search-form"
    >
      <div className="recommend-container" data-testid="tab-recommend">
        <div className="header mt-4">
          <h2 className="fw-semibold">ตั้งค่าเงินค่าคอมมิชชัน</h2>
          <div className="d-flex flex-column gap-2">
            <Row className="gy-3">
              <Col sm={5} md={6} lg={3} xl={2}>
                <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                  <DropdownToggle caret>{selectedOption?.name || "เลือกตัวเลือก"}</DropdownToggle>
                  <DropdownMenu>
                    {options.map((option) => (
                      <DropdownItem key={option.id} onClick={() => handleOptionSelect(option)} className={`${formik.values.filterType === option.id ? "active" : ""}`}>
                        {option.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </Col>
              <Col sm={7} md={6} lg={3} xl={3}>
                {formik.values.filterType === "category" ? (
                  <Dropdown isOpen={categoryDropdownOpen} toggle={toggleCategoryDropdown}>
                    <DropdownToggle caret>{selectedCategory?.name || "เลือกหมวดหมู่"}</DropdownToggle>
                    <DropdownMenu>
                      {categories.map((category) => (
                        <DropdownItem key={category.id} onClick={() => handleCategorySelect(category)} className={`${formik.values.categoryId === category.id ? "active" : ""}`}>
                          {category.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                ) : formik.values.filterType === "brand" ? (
                  <Dropdown isOpen={brandDropdownOpen} toggle={toggleBrandDropdown}>
                    <DropdownToggle caret>{selectedBrand?.name || "เลือกแบรนด์"}</DropdownToggle>
                    <DropdownMenu>
                      {brands.map((brand) => (
                        <DropdownItem key={brand.id} onClick={() => handleBrandSelect(brand)} className={`${formik.values.brandId === brand.id ? "active" : ""}`}>
                          {brand.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                ) : (
                  <></>
                )}
              </Col>
              <Col sm={12} md={12} lg={6} xl={{ offset: 1, size: 6 }}>
                <div className="d-flex gap-3">
                  <div className="flex-grow-1">
                    <div className="input-group-icon">
                      <i className="bx bx-search" />
                      <Input name="searchTerm" type="text" placeholder="ค้นหารายการสินค้า" onChange={(e) => setSearchInput(e.target.value)} value={searchInput} data-testid="search-input" invalid={formik.touched.searchTerm && formik.errors.searchTerm} />
                      {formik.touched.searchTerm && formik.errors.searchTerm && (
                        <div className="error-message" data-testid="search-error">
                          {formik.errors.searchTerm}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <div className="text-lg mt-2 mb-4">ค้นพบรายการสินค้าแนะนำ ในระบบทั้งหมด *{/* {dataHighlight.pagination.total.toLocaleString()} รายการ * */}</div>

        <TabGroup>
          <TabList className="nav nav-tabs nav-tabs-custom">
            <Tab className="nav-link">สินค้าแนะนำ</Tab>
            <Tab className="nav-link">สินค้าทั้งหมด</Tab>
          </TabList>
          <TabPanels className="tab-content">
            <TabPanel>
              <ProductRecomHighlightTable data={dataHighlight} error={errorHighlight} isLoading={isLoadingHighlight} onTableChange={handleTableChange} />
            </TabPanel>
            <TabPanel>
              <ProductRecomNomaroiTable data={dataNomaroi} error={errorNomaroi} isLoading={isLoadingNomaroi} onTableChange={handleTableChange} />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
    </Form>
  );
};

export default ProductRecomTab;
